/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  HostBinding,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UmssIconComponent } from '../icon/icon.component';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'umss-date-picker-input',
  standalone: true,
  imports: [CommonModule, UmssIconComponent, NgxMaskDirective],
  templateUrl: './date-picker-input.component.html',
  styleUrl: './date-picker-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssDatePickerInputComponent),
      multi: true
    },
    provideNgxMask()
  ]
})
export class UmssDatePickerInputComponent
  implements ControlValueAccessor, OnChanges
{
  @HostBinding('style.display') display = 'contents';

  private _generatedId = `umss-date-picker-input-${Math.random().toString(36).substr(2, 9)}`;

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this._value = changes['value'].currentValue;
    }
  }

  /** Content props */
  @Input() id?: string;
  @Input() label?: string;
  @Input() placeholder?: string = 'MM/DD/YYYY';
  @Input() value?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() variant: 'Default' | 'Outlined' = 'Default';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;
  @Input() errorMessage?: string;
  @Input() readonly: boolean = false;
  @Input() clearable: boolean = false;
  @Input() fullWidth: boolean = false;

  /** Input type and validation */
  @Input() inputType: 'date' | 'datetime-local' | 'text' = 'date';
  @Input() min?: string;
  @Input() max?: string;

  /** Event outputs */
  @Output() valueChange = new EventEmitter<string | undefined>();
  @Output() focus = new EventEmitter<FocusEvent>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() input = new EventEmitter<Event>();

  /** Internal state */
  private _value?: string;
  private onChange = (value: string | undefined) => {};
  private onTouched = () => {};

  /** Computed properties */
  get computedId(): string {
    return this.id || this._generatedId;
  }

  get hasValue(): boolean {
    return !!this._value && this._value.trim().length > 0;
  }

  get displayValue(): string {
    if (!this._value) return '';
    if (this.inputType === 'date') {
      return this.isoToMmDdYyyy(this._value);
    }
    return this._value;
  }

  /** Convert ISO (YYYY-MM-DD) to MM/DD/YYYY */
  private isoToMmDdYyyy(value: string): string {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      return `${match[2]}/${match[3]}/${match[1]}`;
    }
    // Already in MM/DD/YYYY or partial — return as-is
    return value;
  }

  /** Convert MM/DD/YYYY to ISO (YYYY-MM-DD) */
  private mmDdYyyyToIso(value: string): string | undefined {
    const clean = value.replace(/[^0-9/]/g, '');
    const match = clean.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      return `${match[3]}-${match[1]}-${match[2]}`;
    }
    return undefined;
  }

  /** Event handlers */
  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value || undefined;

    let newValue: string | undefined;
    if (this.inputType === 'date' && raw) {
      // mask output is MM/DD/YYYY — convert to ISO for the form control
      newValue = this.mmDdYyyyToIso(raw) ?? undefined;
    } else {
      newValue = raw;
    }

    this._value =
      this.inputType === 'date' ? (this.mmDdYyyyToIso(raw ?? '') ?? raw) : raw;
    this.value = newValue;
    this.valueChange.emit(newValue);
    this.onChange(newValue);
    this.input.emit(event);
  }

  onInputFocus(event: FocusEvent): void {
    this.focus.emit(event);
  }

  onInputBlur(event: FocusEvent): void {
    this.onTouched();
    this.blur.emit(event);
  }

  /** Helper methods */
  clear(): void {
    this._value = undefined;
    this.value = undefined;
    this.valueChange.emit(undefined);
    this.onChange(undefined);
  }

  /** ControlValueAccessor implementation */
  writeValue(value: string | undefined): void {
    // Store internally as ISO (YYYY-MM-DD); displayValue getter converts to MM/DD/YYYY
    this._value = value;
    this.value = value;
  }

  registerOnChange(fn: (value: string | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Icon size based on input size */
  get iconSize(): string {
    switch (this.size) {
      case 'Small':
        return '14px';
      case 'Regular':
        return '16px';
      case 'Large':
        return '18px';
      default:
        return '16px';
    }
  }
}
